import { ServerError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { datadogLogs } from '@datadog/browser-logs';
import { GraphQLFormattedError } from 'graphql';

function isGraphQLError(value: unknown): value is GraphQLFormattedError {
  return value !== null && typeof value === 'object' && 'message' in value;
}

export const createErrorLink = (onUnauthenticatedError: () => Promise<void>) =>
  onError(({ forward, graphQLErrors, networkError, operation }) => {
    // We're being defensive here because we've seen situtations where `graphQLErrors` is not
    // actually an array of `GraphQLFormattedError`.
    if (Array.isArray(graphQLErrors)) {
      graphQLErrors.forEach(error => {
        if (isGraphQLError(error)) {
          datadogLogs.logger.error('WebGraphQLLatencyMetric', {
            name: '[GraphQL Error]',
            message: error.message,
            locations: error.locations,
            path: error.path,
          });
        }
      });
    }

    if (networkError && networkError.name === 'ServerError' && (networkError as ServerError).statusCode === 401) {
      // If the network error is a 401, we want to log it and trigger the unauthenticated error handler.
      // This is an "expected" error in the sense that it indicates the user is not authenticated,
      // so we handle it differently than other errors, not capturing it with Sentry
      datadogLogs.logger.error('WebGraphQLNetworkError', {
        name: '[401 Error]',
        message: `operationName: ${operation.operationName} message: ${networkError.message}`,
      });

      return new Observable(observer => {
        onUnauthenticatedError()
          .then(() => {
            observer.complete();
          })
          .catch(error => {
            observer.error(error);
          });
      });
    }

    return forward(operation);
  });
